import React, { useContext } from "react"
import { StyleSheet, View } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { SkeletonBlock, SkeletonCircle, SkeletonGroup, SkeletonLine } from "../utility/skeleton"
import { MAX_CONTENT_WIDTH } from "../config"

export const TransactionsSkeleton = ({
  itemCount = 8,
  numberOfDates,
  transactionsPerDate,
  iconSize = 50,
  valueDotSize = 18,
}: {
  itemCount?: number
  numberOfDates?: number
  transactionsPerDate?: number
  iconSize?: number
  valueDotSize?: number
}): React.ReactElement => {
  const { theme } = useContext(ThemeContext)
  const { bgMain, bgTransaction } = theme.colors
  const clampedCount = Math.max(4, Math.min(14, itemCount))
  const clampedDates = Math.max(1, Math.min(7, numberOfDates || 0))
  const clampedTxPerDate = Math.max(1, Math.min(10, transactionsPerDate || 0))
  const useSectionMode = !!(numberOfDates && transactionsPerDate)

  return (
    <View style={[styles.container, useSectionMode ? { backgroundColor: bgMain } : null]}>
      <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center" }}>
        <SkeletonGroup>
          {useSectionMode ? (
            new Array(clampedDates).fill(0).map((_, dateIdx) => (
              <View key={dateIdx}>
                {/* Section header (matches transactions-by-date SectionList header layout) */}
                <View style={[styles.sectionHeader, { backgroundColor: bgMain }]}>
                  <SkeletonLine width={120} height={14} radius={8} />
                </View>

                {/* Section items */}
                {new Array(clampedTxPerDate).fill(0).map((__, txIdx) => (
                  <View key={txIdx}>
                    <View style={{ paddingHorizontal: 16 }}>
                      <TransactionCardSkeleton
                        bgTransaction={bgTransaction}
                        iconSize={iconSize}
                        valueDotSize={valueDotSize}
                      />
                    </View>
                    {/* ItemSeparatorComponent: height 16 */}
                    {txIdx < clampedTxPerDate - 1 ? <View style={{ height: 16 }} /> : null}
                  </View>
                ))}

                {/* SectionSeparatorComponent: height 4 */}
                {dateIdx < clampedDates - 1 ? <View style={{ height: 4 }} /> : null}
              </View>
            ))
          ) : (
            new Array(clampedCount).fill(0).map((_, idx) => (
              <TransactionCardSkeleton
                key={idx}
                bgTransaction={bgTransaction}
                iconSize={iconSize}
                valueDotSize={valueDotSize}
              />
            ))
          )}
        </SkeletonGroup>
      </View>
    </View>
  )
}

const TransactionCardSkeleton = ({
  bgTransaction,
  iconSize,
  valueDotSize,
}: {
  bgTransaction: string
  iconSize: number
  valueDotSize: number
}) => {
  return (
    <View style={[styles.card, { backgroundColor: bgTransaction }]}>
      <SkeletonBlock style={styles.colorBand} radius={0} />
      <View style={styles.leftCol}>
        <SkeletonBlock style={{ width: iconSize, height: iconSize }} radius={8} />
      </View>
      <View style={styles.midCol}>
        <SkeletonLine width="75%" height={14} radius={6} />
        <SkeletonLine width="55%" height={12} radius={6} />
        <View style={{ flexDirection: "row", gap: 6, marginTop: 2 }}>
          <SkeletonCircle size={valueDotSize} />
          <SkeletonCircle size={valueDotSize} />
          <SkeletonCircle size={valueDotSize} />
        </View>
      </View>
      <View style={styles.rightCol}>
        <SkeletonLine width={70} height={14} radius={6} style={{ alignSelf: "flex-end" }} />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingHorizontal: 10,
    paddingTop: 10,
    flexDirection: "column",
    gap: 10,
  },
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 8,
  },
  card: {
    width: "100%",
    borderRadius: 10,
    overflow: "hidden",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 15,
    padding: 15,
  },
  colorBand: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: 10,
  },
  leftCol: {
    flexDirection: "column",
    justifyContent: "center",
  },
  midCol: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    gap: 8,
  },
  rightCol: {
    width: 90,
    flexDirection: "column",
    justifyContent: "center",
    paddingVertical: 2,
  },
})

